<script setup lang="ts">
import { computed, ref } from 'vue'
import { useDark, useToggle } from '@vueuse/core'

import { useI18n } from 'vue-i18n'

import AppAvatar from '@/components/atoms/avatar/AppAvatar.vue'
import AppThemeSwitcher from '@/components/molecules/theme/AppThemeSwitcher.vue'
import { useAuthStore } from '@/stores/useAuthStore'
import { useLogoutConfirm } from '../composables/useLogoutConfirm'
import AppDialog from '../../dialogs/AppDialog.vue'
import { Palette } from 'lucide-vue-next'

const { t } = useI18n()
const authStore = useAuthStore()
const isDark = useDark({ selector: 'html' })
const toggleDark = useToggle(isDark)
const { confirmLogout: openLogoutConfirm } = useLogoutConfirm()

const showModal = ref(false)

const confirmLogout = () => {
  showModal.value = false
  openLogoutConfirm()
}

const handleAction = (action: (() => void) | undefined) => {
  if (action) {
    action()
  }
}

const userInitials = computed(() => {
  return (
    authStore.user?.name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || 'U'
  )
})

const menuItems = computed(() => [
  {
    label: isDark.value ? t('sidebar.menu.lightMode') : t('sidebar.menu.darkMode'),
    icon: isDark.value ? 'pi pi-sun' : 'pi pi-moon',
    action: () => toggleDark(),
  },
  {
    separator: true,
  },
  {
    label: t('sidebar.menu.logout'),
    icon: 'pi pi-sign-out',
    action: () => confirmLogout(),
  },
])
</script>

<template>
  <div class="relative">
    <div
      @click="showModal = true"
      class="flex cursor-pointer p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
    >
      <AppAvatar
        :label="userInitials"
        :image="authStore.user?.avatar"
        size="large"
        class="ring-2 h-11! w-11! !bg-[var(--p-primary-color)] !text-[var(--p-primary-contrast-color)] [&_img]:h-full [&_img]:w-full [&_img]:object-cover [&_img]:object-center"
        :class="isDark ? 'ring-[var(--p-primary-color)]/20' : 'ring-white/30'"
      />
    </div>

    <AppDialog
      v-model="showModal"
      :modal="true"
      custom-class="backdrop-blur-xl!"
      :closable="true"
      :dismissable-mask="true"
      class="w-80"
      :pt="{
        root: 'user-menu-modal',
      }"
    >
      <template #header>
        <div class="flex items-center gap-3 w-full">
          <AppAvatar
            :label="userInitials"
            :image="authStore.user?.avatar"
            size="normal"
            class="ring-2 !bg-[var(--p-primary-color)] !text-[var(--p-primary-contrast-color)] [&_img]:h-full [&_img]:w-full [&_img]:object-cover [&_img]:object-center"
            :class="
              isDark
                ? 'ring-[var(--p-primary-color)]/20'
                : 'ring-[var(--p-primary-contrast-color)]/30'
            "
          />
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {{ authStore.user?.name || 'Usuario' }}
            </p>
            <p class="text-xs text-gray-500 dark:text-gray-400 truncate">
              {{ authStore.user?.email || '' }}
            </p>
          </div>
        </div>
      </template>

      <!-- Menu items -->
      <div class="space-y-1 pt-2">
        <div
          class="flex items-center justify-between gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-200"
        >
        <div class="flex gap-2">
          <Palette :size="18"></Palette>
          <span>{{ t('header.actions.change_theme') }}</span>

        </div>
          <AppThemeSwitcher />
        </div>

        <template v-for="(item, index) in menuItems" :key="index">
          <div v-if="item.separator" class="my-2 border-t border-gray-200 dark:border-gray-700" />
          <button
            v-else
            @click="handleAction(item.action)"
            class="flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors duration-200 w-full text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <i :class="item.icon" class="text-sm w-4" />
            <span>{{ item.label }}</span>
          </button>
        </template>
      </div>
    </AppDialog>
  </div>
</template>

<style scoped>
:deep(.user-menu-modal) {
  max-width: 20rem;
}

:deep(.user-menu-modal .p-dialog-content) {
  padding: 0 1rem 1rem 1rem;
}
</style>
